import React, { createContext, useContext } from 'react';

/**
 * Where portaled floating UI (ActionDropdown menu, DateRangeFilter panels)
 * should mount (RESPONSIVE_WEB_PLAN, architecture decision 2).
 *
 * `document.body` is wrong while a `<dialog>` opened with `showModal()` is up:
 * the rest of the document is inert and painted under the top layer, so a
 * menu portaled to body is unreachable. `SideBarModal` provides its own
 * untransformed dialog node here; consumers read it with `usePortalTarget()`
 * and fall back to `document.body` outside any provider (or while the dialog
 * ref is still `null` on the first render).
 */
const PortalTargetContext = createContext<HTMLElement | null>(null);

export interface PortalTargetProviderProps {
  value: HTMLElement | null;
  children: React.ReactNode;
}

export const PortalTargetProvider: React.FC<PortalTargetProviderProps> = ({
  value,
  children,
}) => (
  <PortalTargetContext.Provider value={value}>
    {children}
  </PortalTargetContext.Provider>
);

export function usePortalTarget(): HTMLElement {
  return useContext(PortalTargetContext) ?? document.body;
}

export default PortalTargetProvider;
